export interface IBaseHealthcareFacility {
  healthcareFacilityTypeID: number;
  name: string;
  address: string;
  address2: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  contactName: string;
  contactRole: string;
  contactEmail: string;
  contactPhone: string;
  isAnonymizedMode: boolean;
  logoFilename?: string;
}

export interface IClinicBranding {
  name: string;
  logoUrl: string | null;
}

export interface IHealthcareFacility extends IBaseHealthcareFacility {
  id: number;
  createdBy: number;
  isDeleted: boolean;
  therapeuticIndicationIds: number[];
  questionnaireIds: number[];
  featureIds: number[];
  healthcareFacilityTypes: Array<{ id: number; name: string }>;
  createdDate?: string;
  lastModifiedDate?: string;
  lastModifiedBy?: number;
}

export type IHealthcareFacilityForm = Omit<
  IHealthcareFacility,
  | 'createdDate'
  | 'lastModifiedDate'
  | 'lastModifiedBy'
  | 'createdBy'
  | 'isDeleted'
  | 'healthcareFacilityTypes'
> & {
  therapeuticIndicationIds: number[];
  questionnaireIds: number[];
  featureIds: number[];
};

export class HealthcareFacility implements IHealthcareFacility {
  id: number;
  createdBy: number;
  isDeleted: boolean;
  healthcareFacilityTypeID: number;
  name: string;
  address: string;
  address2: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  contactName: string;
  contactRole: string;
  contactEmail: string;
  contactPhone: string;
  isAnonymizedMode: boolean;
  logoFilename?: string;
  therapeuticIndicationIds: number[];
  questionnaireIds: number[];
  featureIds: number[];
  healthcareFacilityTypes: Array<{ id: number; name: string }>;
  createdDate?: string;
  lastModifiedDate?: string;
  lastModifiedBy?: number;

  constructor(input: IHealthcareFacility) {
    this.id = input.id;
    this.createdDate = input.createdDate;
    this.lastModifiedDate = input.lastModifiedDate;
    this.lastModifiedBy = input.lastModifiedBy;
    this.createdBy = input.createdBy;
    this.isDeleted = input.isDeleted;
    this.healthcareFacilityTypeID = input.healthcareFacilityTypeID;
    this.name = input.name;
    this.address = input.address;
    this.address2 = input.address2;
    this.city = input.city;
    this.province = input.province;
    this.country = input.country;
    this.postalCode = input.postalCode;
    this.contactName = input.contactName;
    this.contactRole = input.contactRole;
    this.contactEmail = input.contactEmail;
    this.contactPhone = input.contactPhone;
    this.isAnonymizedMode = input.isAnonymizedMode;
    this.logoFilename = input.logoFilename;
    this.therapeuticIndicationIds = input.therapeuticIndicationIds;
    this.questionnaireIds = input.questionnaireIds;
    this.featureIds = input.featureIds;
    this.healthcareFacilityTypes = input.healthcareFacilityTypes;
  }

  get healthcareFacilitiesCombobox(): Array<{ value: string; label: string }> {
    return this.toCombobox(this.healthcareFacilityTypes);
  }

  private toCombobox(
    items: Array<{ id: number; name: string }> = [],
  ): Array<{ value: string; label: string }> {
    return items.map(({ id, name }) => ({ value: id.toString(), label: name }));
  }
}

export interface IHealthcareFacilityPayload {
  healthcareFacilityTypeID: number;
  name: string;
  address: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  contactName: string;
  contactRole: string;
  contactEmail: string;
  contactPhone: string;
  logoFilename?: string;
}

export interface IHealthcareFacilityDefaultTask {
  id: number;
  healthcareFacilityID: number;
  questionnaireID: number;
  availableDateOffsetDays: number;
  dueDateOffsetDays: number;
  isRecurring: boolean;
  recurrenceType: string | null;
  recurrenceInterval: number;
  recurrenceEndOffsetDays: number | null;
  expirationDays: number | null;
}

export interface IHealthcareFacilityDefaultTaskInput {
  questionnaireID: number;
  availableDateOffsetDays: number;
  dueDateOffsetDays: number;
  isRecurring: boolean;
  recurrenceType: string | null;
  recurrenceInterval: number;
  recurrenceEndOffsetDays: number | null;
  expirationDays: number | null;
}
